import React, { useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  IconButton,
  Typography,
  Paper,
  Tooltip,
  LinearProgress,
  Avatar,
  Chip,
  Box,
  Stack,
} from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { Assessment as AssessmentIcon, EventNote as SessionIcon } from "@mui/icons-material";

// Generates a consistent color from a name string
const stringToColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsl(${h}, 45%, 52%)`;
};

const DataStudentGrid = ({ 
  data,
  viewSessionInfo,
  loading,
  paginationMode,
  paginationModel,
  onPaginationModelChange,
  pageSizeOptions,
  rowCount,
}) => {
  const columns = [
    {
      field: "first_name",
      headerName: "First name",
      flex: 1,
      renderCell: (params) => {
        const { first_name, last_name } = params.row;
        const initials = `${first_name?.[0] ?? ""}${last_name?.[0] ?? ""}`.toUpperCase();
        const bg = stringToColor(`${first_name} ${last_name}`);
        return (
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: bg }}>
              {initials}
            </Avatar>
            <Typography variant="body2">{first_name}</Typography>
          </Stack>
        );
      },
    },
    {
      field: "middle_name",
      headerName: "Middle name",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" color={params.row.middle_name ? "text.primary" : "text.disabled"}>
          {params.row.middle_name || "—"}
        </Typography>
      ),
    },
    {
      field: "last_name",
      headerName: "Last name",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.last_name}</Typography>
      ),
    },
    {
      field: "session_count",
      headerName: "Sessions",
      flex: 1,
      renderCell: (params) => (
        <Chip
          icon={<SessionIcon color="inherit" sx={{ fontSize: "14px !important" }} />}
          label={params.row.session_count ?? 0}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: "assessment_count",
      headerName: "Assessments",
      flex: 1,
      renderCell: (params) => (
        <Chip
          icon={<AssessmentIcon sx={{ fontSize: "14px !important" }} />}
          label={params.row.assessment_count ?? 0}
          size="small"
          variant="outlined"
          color="secondary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Session info",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="View session data" placement="right">
          <IconButton
            color="secondary"
            size="small"
            onClick={() => viewSessionInfo(params.row.id)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              "&:hover": { borderColor: "secondary.main" },
            }}
          >
            <AnalyticsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const [pageSize, setPageSize] = useState(10);

  return (
    <Paper
      elevation={0}
      sx={{
        height: "calc(100vh - 350px)",
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <LinearProgress sx={{ visibility: loading ? "visible" : "hidden" }} />
      <DataGrid
        rows={data}
        columns={columns}
        pageSize={pageSize}
        rowCount={rowCount}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        pagination
        disableSelectionOnClick
        disableColumnMenu
        rowHeight={52}
        paginationMode={paginationMode}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={pageSizeOptions}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "action.hover",
            borderBottom: "1px solid",
            borderColor: "divider",
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "text.secondary",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "action.hover",
          },
          
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            borderColor: "divider",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid",
            borderColor: "divider",
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
        }}
      />
    </Paper>
  );
};

export default DataStudentGrid;